import React, { useMemo } from 'react'
import { Trans } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useIsEnhancedPostPaymentFlow } from 'hooks/useIsEnhancedPostPaymentFlow'
import { usePageConfig } from 'hooks/usePageConfig'
import { usePostPaymentFlowConfig } from 'hooks/usePostPaymentFlowConfig'

import { getPageIdFromPathName } from 'helpers/getPageIdFromPathName'

import { StyledPostPaymentProgressBar as S } from './PostPaymentProgressBar.styles'

const INITIAL_PROGRESS_VALUE = 0
const PROGRESS_SECTION_STEP = 1

export const PostPaymentProgressBar: React.FC = () => {
  const { pathname } = useLocation()
  const shouldShow = useIsEnhancedPostPaymentFlow()
  const { progressSteps, finalStep } = usePostPaymentFlowConfig()
  const { isHeaderOverlay, progressTrackColor } = usePageConfig()

  const currentPageId = useMemo(
    () => getPageIdFromPathName(pathname),
    [pathname],
  )

  const currentSectionIndex = useMemo(() => {
    const index = progressSteps.findIndex(
      (block) => !!block.find((step) => step.id === currentPageId),
    )

    return index === -1 ? 0 : index
  }, [currentPageId, progressSteps])

  if (!shouldShow) return null

  return (
    <S.Wrapper $isHeaderOverlay={isHeaderOverlay}>
      {progressSteps.map(([{ id, text }], index, arr) => {
        const progressValue =
          currentSectionIndex >= index ? arr.length : INITIAL_PROGRESS_VALUE
        const nextSectionIndex = currentSectionIndex + PROGRESS_SECTION_STEP

        return (
          <S.StepBlock key={id}>
            <S.Step
              $isActive={nextSectionIndex === index}
              $isPassed={currentSectionIndex >= index}
              $trackColor={progressTrackColor}
            >
              <S.StepText>
                <Trans i18nKey={text} />
              </S.StepText>
            </S.Step>

            <S.StepBlockProgress
              value={progressValue}
              max={arr.length}
              $pointsNumber={arr.length}
              $trackColor={progressTrackColor}
            />
          </S.StepBlock>
        )
      })}

      <S.Step
        $isActive={currentPageId === finalStep.id}
        $trackColor={progressTrackColor}
      >
        <S.StepText>
          <Trans i18nKey={finalStep.text} />
        </S.StepText>
      </S.Step>
    </S.Wrapper>
  )
}
